import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Alert, message, Space } from "antd";
import { useRobotPeerConnection } from "features/robot-teleop/contexts/RobotConnectionContext";
import VideoTrackPlayer from "features/robot-teleop/components/video-viewer/VideoTrackPlayer";
import logger from "shared/utils/logging";

const RobotPeerVideoViewer = ({
  PeerID,
  ShowDelay = false,
  ShowStatus = false,
  ShowAlert = true,
  Width = "100%",
  Height = "100%",
  onChannelMessage,
}) => {
  const peerId = PeerID;

  const qsShowDelayGraph = ShowDelay;

  const qsShowStatus = ShowStatus;
  const showAlert = ShowAlert;

  const qsCamWidth = Width;
  const qsCamHeight = Height;

  const [messageApi, contextHolder] = message.useMessage();

  // Memoize the channel message handler to prevent unnecessary re-renders
  const handleChannelMessage = useCallback(
    (msg) => {
      if (onChannelMessage && typeof onChannelMessage === "function") {
        onChannelMessage(msg);
      }
    },
    [onChannelMessage]
  );

  const {
    setRobotId,
    rtcConnectionState,
    mediaTracks,
    error,
    robotOnlineStatus,
    setStatsEnabled,
    isSendingAudio,
    setIsSendingAudio,
    changeRemoteVideoConfig,
    isReceiveVideo,
    isReceiveAudio,
    setIsReceiveVideo,
    setIsReceiveAudio,
    allowLocalAudio,
    allowRemoteAudio,
    allowRemoteVideo,
    mediaAllowed,
    mediaError,
    addChannelMessageReceivedEventListener,
  } = useRobotPeerConnection();

  const getVideoStream = useCallback((trackEvents, index = 0) => {
    logger.log("get video stream: index=", index);
    if (!trackEvents) return null;

    const tracks = Array.isArray(trackEvents) ? trackEvents : [trackEvents];
    let count = 0;

    for (const evt of tracks) {
      if (evt?.track?.kind === "video") {
        if (count === index) {
          logger.log(
            "found video stream ",
            index,
            " at ",
            count,
            "track",
            evt
          );
          return evt.streams?.[0] || null;
        }
        count++;
      }
    }
    return null;
  }, []);

  const getAudioStream = useCallback((trackEvents, index = 0) => {
    logger.log("get audio stream: index=", index);
    if (!trackEvents) return null;

    const tracks = Array.isArray(trackEvents) ? trackEvents : [trackEvents];
    let count = 0;

    for (const evt of tracks) {
      if (evt?.track?.kind === "audio") {
        if (count === index) {
          logger.log(
            "found audio stream ",
            index,
            " at ",
            count,
            "track",
            evt
          );
          return evt.streams?.[0] || null;
        }
        count++;
      }
    }
    return null;
  }, []);

  // const videoStream = getVideoStream(mediaTracks);
  // const audioStream = getAudioStream(mediaTracks);

  const videoStream = useMemo(() => {
    if (!mediaTracks) return null;
    return getVideoStream(mediaTracks);
  }, [mediaTracks]);

  const audioStream = useMemo(() => {
    if (!mediaTracks) return null;
    return getAudioStream(mediaTracks);
  }, [mediaTracks]);

  useEffect(() => {
    logger.log("RobotPeerVideoViewer useEffect setRobotId: ", peerId);
    setRobotId(peerId);
    const unlisten =
      addChannelMessageReceivedEventListener(handleChannelMessage);
    return () => {
      // Ensure cleanup function is called properly
      if (unlisten && typeof unlisten === "function") {
        unlisten();
      }
    };
  }, [peerId, handleChannelMessage]);

  useEffect(() => {
    setStatsEnabled(qsShowDelayGraph);
  }, [qsShowDelayGraph]);

  // Cleanup effect for component unmount
  useEffect(() => {
    return () => {
      logger.log("RobotPeerVideoViewer cleanup on unmount");
      // Additional cleanup can be added here if needed
      // The VideoTrackPlayer component will handle its own cleanup
    };
  }, []);

  // const divStyle = { width: qsCamWidth, height: qsCamHeight };
  // logger.log("divStyle: ", divStyle);

  const mainDivStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    // height: "100vh",
    width: "100%",
    margin: "0",
  };

  const alertDivStyle = {
    position: "fixed",
    top: "25%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 9999,
    // backgroundColor: "rgba(0, 0, 0, 0.5)", // Optional: Add a semi-transparent background
    width: "80%",
    maxWidth: "600px",
    padding: "20px",
    borderRadius: "8px",
  };

  const onRemoteVideoToggleClicked = (value) => {
    logger.log("Remote Video toggle clicked: ", value);
    setIsReceiveVideo(!isReceiveVideo);
  };

  const onRemoteMicrophoneToggleClicked = (value) => {
    logger.log("Remote Microphone toggle clicked: ", value);
    setIsReceiveAudio(!isReceiveAudio);
  };

  const onLocalMicrophoneToggleClicked = (value) => {
    logger.log("Local Microphone toggle clicked: ", value);
    setIsSendingAudio(!isSendingAudio);
  };
  const onCameraConfigChanged = (config) => {
    logger.log("Camera config changed: ", config);
    // Handle camera configuration changes here if needed
    changeRemoteVideoConfig(config)
      .then((result) => {
        logger.log("Camera configuration updated: ", result);
        messageApi.success("Camera configuration updated.");
      })
      .catch((err) => {
        messageApi.error("Camera configuration update failed: " + err);
      });
  };

  return (
    <div style={mainDivStyle}>
      <div>
        {showAlert && (
          <div
            style={{
              ...alertDivStyle,
              display:
                rtcConnectionState !== "connected" || !robotOnlineStatus
                  ? "flex"
                  : "none",
            }}
          >
            <Space
              direction="vertical"
              style={{
                width: "100%",
              }}
            >
              {!robotOnlineStatus && (
                <Alert
                  message={`${peerId} is OFFLINE`}
                  type="warning"
                  showIcon
                  closable
                />
              )}
              {rtcConnectionState &&
                (rtcConnectionState === "failed" ||
                  rtcConnectionState === "disconnected") && (
                  <Alert
                    message={`Peer connection to ${peerId} is '${rtcConnectionState}'. ${
                      robotOnlineStatus
                        ? "Reconnecting ...!"
                        : "Auto-reconnect when robot is ONLINE"
                    }`}
                    type="error"
                    showIcon
                    closable
                  />
                )}
              {rtcConnectionState &&
                robotOnlineStatus &&
                rtcConnectionState !== "failed" &&
                rtcConnectionState !== "disconnected" &&
                rtcConnectionState !== "connected" && (
                  <Alert
                    message={`(Re-)connecting to '${peerId}'. Connection state: ${rtcConnectionState}`}
                    type="error"
                    showIcon
                    closable
                  />
                )}
            </Space>
          </div>
        )}

        <div
        // style={divStyle}
        >
          {contextHolder}
          <VideoTrackPlayer
            CameraStyle={{ height: qsCamHeight, width: qsCamWidth }}
            videoMediaStream={videoStream}
            audioMediaStream={audioStream}
            ShowDelayTime={qsShowDelayGraph}
            allowLocalAudio={allowLocalAudio}
            allowRemoteAudio={allowRemoteAudio}
            allowRemoteVideo={allowRemoteVideo}
            isReceiveVideo={isReceiveVideo}
            isReceiveAudio={isReceiveAudio}
            isSendingAudio={isSendingAudio}
            mediaAllowed={mediaAllowed}
            mediaError={mediaError}
            onVideoToggleClicked={onRemoteVideoToggleClicked}
            onAudioToggleClicked={onRemoteMicrophoneToggleClicked}
            onLocalMicToggleClicked={onLocalMicrophoneToggleClicked}
            onCameraConfigChanged={onCameraConfigChanged}
          ></VideoTrackPlayer>
        </div>
      </div>
    </div>
  );
};

export default RobotPeerVideoViewer;
